import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { monsterApi } from '../../../api/adminApi';
import { useTranslation } from '../../../i18n/useTranslation';
import type { MonsterTemplate } from '../../../types/adminTypes';

export function MonstersPage() {
  const [monsters, setMonsters] = useState<MonsterTemplate[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  
  // Search & filter
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'level' | 'hp' | 'xp'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [guideOpen, setGuideOpen] = useState(false);
  
  const navigate = useNavigate();
  const { t } = useTranslation();

  const load = async () => {
    try {
      setLoading(true);
      setMonsters(await monsterApi.list());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`${t('monsters.delete.confirm')} "${name}"?`)) return;
    try {
      await monsterApi.delete(id);
      setMonsters((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      alert(`${t('common.error')} ${(e as Error).message}`);
    }
  };

  const filteredMonsters = useMemo(() => {
    let result = [...monsters];
    
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(m => m.name.toLowerCase().includes(q));
    }
    
    result.sort((a, b) => {
      let valA, valB;
      switch (sortBy) {
        case 'level': valA = a.level; valB = b.level; break;
        case 'hp': valA = a.hp_max; valB = b.hp_max; break;
        case 'xp': valA = a.xp_reward; valB = b.xp_reward; break;
        case 'name':
        default: valA = a.name; valB = b.name; break;
      }
      
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [monsters, search, sortBy, sortOrder]);

  return (
    <>
      <div className="adminHeader" style={{ position: 'relative' }}>
        <h1>{t('monsters.title')}</h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button 
            title={t('guide.scaling.btn')}
            onClick={() => setGuideOpen(true)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 28, padding: 0,
              transition: 'transform 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center',
              lineHeight: 1
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            📚
          </button>
          <button className="btn btnPrimary" onClick={() => navigate('/admin/monsters/new')}>
            ✨ {t('monsters.new')}
          </button>
        </div>
      </div>

      {guideOpen && (
        <div className="modalOverlay" onClick={() => setGuideOpen(false)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.8)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(4px)'
        }}>
          <div className="modalContent" onClick={e => e.stopPropagation()} style={{
            background: 'var(--panel-bg)', border: '1px solid var(--border)',
            padding: 32, borderRadius: 16, maxWidth: 600, width: '90%',
            boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
            maxHeight: '90vh', overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span>⚔️</span> {t('guide.scaling.title')}
            </h2>
            
            <p style={{ color: 'var(--text-dim)', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: t('guide.scaling.intro') }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 24 }}>
              <div className="adminCard" style={{ padding: 16, borderLeft: '4px solid #FF3366' }}>
                <h3 style={{ margin: '0 0 8px 0', color: 'var(--text)' }}>🩸 {t('guide.scaling.hpTitle')}</h3>
                <p style={{ margin: 0 }} dangerouslySetInnerHTML={{ __html: t('guide.scaling.hpDesc') }} />
              </div>

              <div className="adminCard" style={{ padding: 16, borderLeft: '4px solid #33CCFF' }}>
                <h3 style={{ margin: '0 0 8px 0', color: 'var(--text)' }}>👥 {t('guide.scaling.partyTitle')}</h3>
                <p style={{ margin: 0 }} dangerouslySetInnerHTML={{ __html: t('guide.scaling.partyDesc') }} />
              </div>

              <div className="adminCard" style={{ padding: 16, borderLeft: '4px solid #FFCC00' }}>
                <h3 style={{ margin: '0 0 8px 0', color: 'var(--text)' }}>👑 {t('guide.scaling.bossTitle')}</h3>
                <p style={{ margin: 0 }} dangerouslySetInnerHTML={{ __html: t('guide.scaling.bossDesc') }} />
              </div>
            </div>
            
            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <button className="btn btnPrimary" onClick={() => setGuideOpen(false)}>{t('guide.attributes.understood')}</button>
            </div>
          </div>
        </div>
      )}

      <div className="adminContent">
        {error && <div className="alertError">{error}</div>}

        {/* ── Filters ── */}
        <div className="adminCard" style={{ marginBottom: 16 }}>
          <div style={{ padding: '12px 16px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              className="formInput"
              placeholder="Pesquisar monstro por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 200, maxWidth: 340 }}
            />

            <select
              className="formInput"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              style={{ width: 'auto', paddingRight: 28 }}
            >
              <option value="name">Ordenar por Nome</option>
              <option value="level">Ordenar por Nível</option>
              <option value="hp">Ordenar por HP</option>
              <option value="xp">Ordenar por XP</option>
            </select>

            <select
              className="formInput"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as typeof sortOrder)}
              style={{ width: 'auto', paddingRight: 28 }}
            >
              <option value="asc">Crescente</option>
              <option value="desc">Decrescente</option>
            </select>

            <span style={{ fontSize: 12, color: 'var(--text-dim)', marginLeft: 'auto' }}>
              {filteredMonsters.length} resultado{filteredMonsters.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="adminCard">
          <div className="adminCardHeader">
            <span>{t('monsters.listHeader')} ({monsters.length})</span>
          </div>

          {loading ? (
            <div className="spinner" />
          ) : filteredMonsters.length === 0 ? (
            <div className="emptyState">
              {search ? 'Nenhum monstro encontrado para a pesquisa.' : t('monsters.empty')}{' '}
              {!search && <Link to="/admin/monsters/new" style={{ color: 'var(--accent)' }}>{t('monsters.createFirst')}</Link>}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="adminTable" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-dim)' }}>{t('monsters.col.name')}</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-dim)' }}>{t('monsters.col.level')}</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-dim)' }}>{t('monsters.col.hp')}</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-dim)' }}>{t('monsters.col.apMp')}</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-dim)' }}>{t('monsters.col.initiative')}</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-dim)' }}>{t('monsters.col.xp')}</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-dim)' }}>{t('monsters.col.spells')}</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-dim)' }}>{t('monsters.col.ai')}</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-dim)' }}>Status</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-dim)' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMonsters.map((m) => (
                    <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{m.name}</td>
                      <td style={{ padding: '10px 14px' }}><span className="badge">{m.level}</span></td>
                      <td style={{ padding: '10px 14px' }}>{m.hp_max}</td>
                      <td style={{ padding: '10px 14px' }}>{m.ap_max} / {m.mp_max}</td>
                      <td style={{ padding: '10px 14px' }}>{m.initiative}</td>
                      <td style={{ padding: '10px 14px' }}>{m.xp_reward}</td>
                      <td style={{ padding: '10px 14px' }}>
                        {m.spell_ids.length > 0
                          ? m.spell_ids.map((id) => (
                              <span key={id} className="badge" style={{ marginRight: 4 }}>{id}</span>
                            ))
                          : <span style={{ color: 'var(--text-dim)' }}>—</span>
                        }
                      </td>
                      <td style={{ padding: '10px 14px' }}><span className="badge">{m.ai_behavior}</span></td>
                      <td style={{ padding: '10px 14px' }}>
                        <span className="badge" style={{ 
                          background: m.active ?? true ? 'rgba(68, 255, 68, 0.15)' : 'rgba(255, 68, 68, 0.15)',
                          color: m.active ?? true ? '#44ff44' : '#ff4444' 
                        }}>
                          {m.active ?? true ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="btn btnGhost"
                            style={{ padding: '4px 10px', fontSize: 12 }}
                            onClick={() => navigate(`/admin/monsters/${m.id!}/edit`)}
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            className="btn btnDanger"
                            style={{ padding: '4px 10px', fontSize: 12 }}
                            onClick={() => void handleDelete(m.id!, m.name)}
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

